import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap } from 'rxjs/operators';
import { AuthenticationService } from 'src/app/modules/authentication/services/authentication.service';
import { environment } from 'src/environments/environment';
import Language from '../models/language.enum';
import { MobileErrorHandler } from '../utils/mobile-error-handler';

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  private activeRefresh$: Observable<unknown> | null = null;

  constructor(
    @Inject(LOCALE_ID) public locale: Language,
    private authenticationService: AuthenticationService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.url.includes('/token')) {
      return next.handle(request);
    }
    if (!this.authenticationService.isAuthenticated() && this.authenticationService.canRefresh())
      return this.handleRefresh(request, next);

    return this.defaultHandler(request, next);
  }

  private defaultHandler(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    isRetryAfterRefresh = false
  ): Observable<HttpEvent<unknown>> {
    if (request.headers.get('skip')) {
      return next.handle(request);
    }
    const endpoint = request.headers.get('endpoint') ?? environment.endpoint;
    const accessToken = this.authenticationService.getAccessToken();
    let headers = request.headers
      .delete('endpoint')
      .delete('enctype')
      .set('Authorization', `Bearer ${accessToken}`)
      .set('Accept-Language', this.locale);
    // Only overwrite if not sending any files.. lol
    if (request.headers.get('enctype') == null) {
      headers = headers.set('Content-Type', 'application/json');
    }
    return next.handle(request.clone({ headers, url: endpoint + request.url })).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse) {
          switch (error.status) {
            case HttpStatusCode.Unauthorized:
              if (isRetryAfterRefresh) {
                let forbiddenMessage: string = 'no access';
                let forbiddenError: HttpErrorResponse = new HttpErrorResponse({
                  status: HttpStatusCode.Unauthorized,
                  error: forbiddenMessage,
                });
                MobileErrorHandler.throwToMobile(forbiddenError);
                return throwError(() => forbiddenMessage);
              }
              return this.handle401Error(request, next);
            default:
              return throwError(() => error);
          }
        } else {
          return throwError(() => error);
        }
      })
    );
  }

  private handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.authenticationService.canRefresh()) {
      return this.handleRefresh(request, next);
    }
    let unauthorizedMessage: string = 'not authenticated';
    let unauthorizedError: HttpErrorResponse = new HttpErrorResponse({
      status: HttpStatusCode.Forbidden,
      error: unauthorizedMessage,
    });
    MobileErrorHandler.throwToMobile(unauthorizedError);
    return throwError(() => unauthorizedMessage);
  }

  private handleRefresh(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.activeRefresh$) {
      this.activeRefresh$ = this.authenticationService.refreshToken$().pipe(
        finalize(() => (this.activeRefresh$ = null)),
        shareReplay(1)
      );
    }
    return this.activeRefresh$.pipe(switchMap(() => this.defaultHandler(request, next, true)));
  }
}
